import React, { useEffect, useState, useMemo } from 'react';
import { MultiSelectCheckboxTag } from 'lift-ui';
import { Badge, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, SpinnerLoader } from 'lift-ui';
import { HelpCircle } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import apiClient from '@lift-admin/utils/apiClient';

interface BrandMultiSelectProps {
  defaultBrands: number[];
  setDefaultBrands: any
}

// Helper function to parse and normalize brand IDs
const normalizeBrandId = (id: any): number => {
  if (id === undefined || id === null) return 0;
  return typeof id === 'number' ? id : parseInt(id, 10);
};

const BrandMultiSelect = ({ defaultBrands, setDefaultBrands }: BrandMultiSelectProps) => {
  console.log('Current defaultBrands:', defaultBrands);
  
  const [allBrands, setAllBrands] = useState<any[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Determine the initial mode based on the defaultBrands
  const initialMode = useMemo(() => {
    if (!defaultBrands || !Array.isArray(defaultBrands) || defaultBrands.length === 0) return 'exclude';
    
    // If any positive ID exists, we're in include mode
    return defaultBrands.some(id => normalizeBrandId(id) > 0) ? 'include' : 'exclude';
  }, []);
  
  const [mode, setMode] = useState<'exclude' | 'include'>(initialMode);
  
  // Determine if we're in mixed mode (has both positive and negative IDs)
  const hasMixedMode = useMemo(() => {
    if (!defaultBrands || !Array.isArray(defaultBrands)) return false;
    
    const hasPositive = defaultBrands.some(id => normalizeBrandId(id) > 0);
    const hasNegative = defaultBrands.some(id => normalizeBrandId(id) < 0);
    
    return hasPositive && hasNegative;
  }, [defaultBrands]);

  const getAllBrands = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/brand/v1/user/brand/all`);
      const brandsData = res.data.response.map((brand: any) => ({
        value: brand.id,
        label: brand.name,
        id: brand.id,
        name: brand.name
      }));
      setAllBrands(brandsData);
      setLoading(false);
    } catch (error) {
      console.log(error, 'error-getall');
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllBrands();
  }, []);

  // Update selectedBrands when defaultBrands or allBrands change
  useEffect(() => {
    if (!allBrands?.length || !defaultBrands?.length) {
      setSelectedBrands([]);
      return;
    }

    // Map brand IDs to their absolute values for matching
    const brandIdToType: Record<string, 'include' | 'exclude'> = {}; // Store whether ID is positive or negative
    defaultBrands.forEach(id => {
      const normalizedId = normalizeBrandId(id);
      const absId = Math.abs(normalizedId);
      brandIdToType[absId.toString()] = normalizedId > 0 ? 'include' : 'exclude';
    });
    
    // Get the absolute values of defaultBrands for matching with allBrands
    const absIds = defaultBrands.map(id => Math.abs(normalizeBrandId(id)));
    
    // Find the corresponding brand objects
    const matchedBrands = allBrands.filter(brand => {
      const brandId = Math.abs(normalizeBrandId(brand.id));
      return absIds.includes(brandId);
    });
    
    // Filter to show only brands that match the current mode
    const relevantBrands = matchedBrands.filter(brand => {
      const brandId = Math.abs(normalizeBrandId(brand.id));
      const brandIdKey = brandId.toString();
      return (mode === 'exclude' && brandIdToType[brandIdKey] === 'exclude') || 
             (mode === 'include' && brandIdToType[brandIdKey] === 'include');
    });
    
    setSelectedBrands(relevantBrands);
  }, [defaultBrands, allBrands, mode]);

  const handleBrandChange = (selectedValues: string[]) => {
    // Convert string values back to brand objects
    const newValue = selectedValues.map((valueStr) => {
      const brandId = parseInt(valueStr, 10);
      return allBrands.find(brand => Math.abs(normalizeBrandId(brand.id)) === Math.abs(brandId));
    }).filter(Boolean) as any[];
    
    setSelectedBrands(newValue);
    
    // Create new IDs array with the appropriate sign based on current mode only
    const newModeIds = newValue.map((brand: any) => {
      const brandId = Math.abs(normalizeBrandId(brand.id));
      return mode === 'exclude' ? -brandId : brandId;
    });
    
    // Only set the current mode IDs, don't try to preserve opposite mode
    setDefaultBrands('brands_exclude', newModeIds);
  };

  const handleModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newMode = event.target.value as 'exclude' | 'include';
    
    // Don't reset the defaultBrands or filter out brands when changing modes
    // Just update the UI mode
    setMode(newMode);
    setSelectedBrands([]);
    setDefaultBrands('brands_exclude', []);
    
    // Re-select brands based on new mode (will trigger useEffect)
  };
  
  // Filter the available brands to exclude already selected brands (regardless of +/- sign)
  const filteredOptions = useMemo(() => {
    if (!allBrands?.length) return [];
    if (!defaultBrands || !Array.isArray(defaultBrands) || defaultBrands.length === 0) return allBrands;
    
    // Create a set of absolute IDs that are already selected in either mode
    const selectedAbsIds = new Set(
      defaultBrands.map(id => Math.abs(normalizeBrandId(id)))
    );
    
    // Filter out brands that are already selected (by absolute ID)
    return allBrands.filter(brand => {
      const brandId = Math.abs(normalizeBrandId(brand.id));
      return !selectedAbsIds.has(brandId);
    });
  }, [allBrands, defaultBrands]);
  
  // Convert selectedBrands to string array for MultiSelectTag
  const selectedValues = useMemo(() => {
    if (!selectedBrands || !Array.isArray(selectedBrands) || selectedBrands.length === 0) return [];
    return selectedBrands
      .filter(brand => brand && brand.id !== undefined)
      .map(brand => Math.abs(normalizeBrandId(brand.id)).toString());
  }, [selectedBrands]);

  // Convert ALL brands to MultiSelectTag format (needed to display selected tags)
  const multiSelectOptions = useMemo(() => {
    return allBrands.map(brand => ({
      label: brand.label || brand.name || '',
      value: Math.abs(normalizeBrandId(brand.id)).toString()
    }));
  }, [allBrands]);

  // Show appropriate label based on mode
  const autocompleteLabel = mode === 'exclude' 
    ? "Select brands to exclude" 
    : "Select brands to include";
    
  // Show appropriate placeholder text based on mode
  const autocompletePlaceholder = mode === 'exclude'
    ? selectedBrands.length === 0 ? "All brands included" : ""
    : selectedBrands.length === 0 ? "No brands included" : "";
  
  return (
    <div className="mt-4">
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Label className="text-sm font-medium">Brand Access Mode</Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-4 w-4 text-gray-500 cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Exclude mode: Remove access for only specific brands (negative IDs). Include mode: Grant access to only specific brands (positive IDs).</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        
        {hasMixedMode && (
          <p className="text-sm text-red-600 mb-2">
            Warning: Your current configuration has mixed positive and negative IDs. 
            This is not recommended. Please choose one mode.
          </p>
        )}
        
        <RadioGroup
          value={mode}
          onValueChange={(value) => handleModeChange({ target: { value } } as React.ChangeEvent<HTMLInputElement>)}
          className="flex flex-row gap-6"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="exclude" id="exclude" />
            <Label htmlFor="exclude" className="flex items-center gap-2 cursor-pointer">
              Exclude specific brands
              <Badge variant={mode === 'exclude' ? 'destructive' : 'secondary'} className="h-5 text-xs">
                Example
              </Badge>
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="include" id="include" />
            <Label htmlFor="include" className="flex items-center gap-2 cursor-pointer">
              Include only specific brands
              <Badge variant={mode === 'include' ? 'default' : 'secondary'} className="h-5 text-xs bg-primary text-primary-foreground">
                Example
              </Badge>
            </Label>
          </div>
        </RadioGroup>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <SpinnerLoader className="h-6 w-6" />
        </div>
      ) : (
        <MultiSelectCheckboxTag
          placeholder={autocompletePlaceholder || autocompleteLabel}
          value={selectedValues}
          onChange={handleBrandChange}
          options={multiSelectOptions}
          containerClassName="w-full"
          showSelectAll
        />
      )}
      
      <p className="text-sm text-gray-600 mt-2">
        {mode === 'exclude' 
          ? "If no brands are selected, all brands will have access." 
          : "If no brands are selected, no brands will have access."}
      </p>
    </div>
  );
};

export default BrandMultiSelect;